import React from 'react'
import { useEditorStore } from '../state/editor-store'
import { updatePodcast } from '../../../api'
import { updatePodcastLine } from '../../../api/podcasts'
import { PodcastStatusEnum } from '../../../types/enums'
import type { PodcastLine } from '../../../types/podcast'

export const useContextActions = (
  _manager: unknown,
  refetch: () => Promise<unknown>,
  refetchVoices: () => Promise<unknown>,
  podcastId: string
) => {
  const store = useEditorStore()

  const actions = React.useMemo(() => ({
    setHostAVoiceId: async (val: string) => {
      store.setHostAVoiceId(val)
      if (podcastId) await updatePodcast(podcastId, { hostAVoiceId: val })
      await Promise.all([refetch(), refetchVoices()])
    },
    setHostBVoiceId: async (val: string) => {
      store.setHostBVoiceId(val)
      if (podcastId) await updatePodcast(podcastId, { hostBVoiceId: val })
      await Promise.all([refetch(), refetchVoices()])
    },
    setSpeed: async (val: number) => {
      store.setSpeed(val)
      if (podcastId) await updatePodcast(podcastId, { speed: val })
    },
    setVolume: async (val: number) => {
      store.setVolume(val)
      if (podcastId) await updatePodcast(podcastId, { volume: val })
    },
    setEmotion: async (val: string) => {
      store.setEmotion(val)
      if (podcastId) await updatePodcast(podcastId, { emotion: val })
    },
    setLanguageIso: async (iso: string) => {
      const prev = store.languageIso
      const normalized = iso.toUpperCase()
      if (prev === normalized) return
      // Instead of updating language on existing podcast (blocked server-side), trigger clone flow.
      // UI layer should have already confirmed. Here we optimistically keep previous language until clone succeeds.
      const currentPodcast = useEditorStore.getState().podcast
      if (!podcastId || !currentPodcast) {
        // No podcast context; just update local
        store.setLanguageIso(normalized)
        return
      }
      try {
        const { clonePodcastToLanguage } = await import('../../../api/podcasts')
        // All properties (style, duration, voices) are now automatically cloned by the backend
        const res = await clonePodcastToLanguage(podcastId, {
          languageIso: normalized
        })
        // After cloning, navigation responsibility: emit custom event so consuming component can redirect.
        window.dispatchEvent(new CustomEvent('podcast:cloned', { detail: res }))
      } catch (e) {
        // Surface error via custom event
        const errorMessage = e instanceof Error ? e.message : 'Failed to clone podcast to new language.'
        window.dispatchEvent(new CustomEvent('podcast:clone-error', { detail: { message: errorMessage } }))
      }
    },
    beginEditTitle: () => { store.setEditingTitle(true); store.setTitleDraft(store.podcast?.title ?? '') },
    cancelEditTitle: () => { store.setEditingTitle(false); store.setTitleDraft(store.podcast?.title ?? '') },
    setTitleDraft: (v: string) => { store.setTitleDraft(v) },
    saveTitle: async () => {
      const title = store.titleDraft
      if (!podcastId) return
      await updatePodcast(podcastId, { title })
      await refetch()
      store.setEditingTitle(false)
    },
    refetchAll: async () => { await Promise.all([refetch(), refetchVoices()]) },
    setLineText: async (lineId: string, text: string) => {
      // optimistic update
      useEditorStore.setState((s) => {
        const line = s.podcast?.podcastLines?.find(l => l.id === lineId)
        if (line) line.text = text
      })
      if (podcastId) await updatePodcastLine(podcastId, lineId, { text })
      await refetch()
    },
    deleteLine: async (lineId: string) => {
      if (!podcastId) return
      const stateBefore = useEditorStore.getState().podcast
      const snapshot = stateBefore ? { ...stateBefore, podcastLines: [...stateBefore.podcastLines] } : null
      // Disallow delete if processing
      const target = snapshot?.podcastLines.find(l => l.id === lineId)
      if (target?.status === PodcastStatusEnum.Processing) return
      // optimistic remove
      useEditorStore.setState(s => {
        if (!s.podcast) return
        s.podcast.podcastLines = s.podcast.podcastLines.filter(l => l.id !== lineId)
        // resequence locally
        s.podcast.podcastLines.forEach((l, idx) => { l.sequenceOrder = idx + 1 })
        // clear assembled audio url
        s.podcast.audioFileUrl = null
      })
      try {
        const { deletePodcastLine } = await import('../../../api/podcasts')
        await deletePodcastLine(podcastId, lineId)
      } catch {
        // revert
        if (snapshot) {
          useEditorStore.setState(s => { s.podcast = snapshot })
        }
        return
      }
      await refetch()
    },
    addLineBelow: async (afterLineId: string) => {
      if (!podcastId) return
      const stateBefore = useEditorStore.getState().podcast
      const snapshot = stateBefore ? { ...stateBefore, podcastLines: [...stateBefore.podcastLines] } : null
      const target = snapshot?.podcastLines.find(l => l.id === afterLineId)
      if (!target) return
      const tempId = crypto.randomUUID()
      useEditorStore.setState(s => {
        if (!s.podcast) return
        const seqInsert = target.sequenceOrder + 1
        s.podcast.podcastLines.forEach(l => {
          if (l.sequenceOrder >= seqInsert) l.sequenceOrder += 1
        })
        s.podcast.podcastLines.push({
          id: tempId,
          host: target.host,
          text: '',
          audioFileUrl: null,
          status: PodcastStatusEnum.Draft,
          sequenceOrder: seqInsert,
        } as PodcastLine)
        s.podcast.podcastLines.sort((a, b) => a.sequenceOrder - b.sequenceOrder)
        s.podcast.audioFileUrl = null
      })
      try {
        const { createPodcastLineBelow } = await import('../../../api/podcasts')
        await createPodcastLineBelow(podcastId, afterLineId, {})
      } catch {
        if (snapshot) useEditorStore.setState(s => { s.podcast = snapshot })
        return
      }
      await refetch()
    },
    setLineHost: async (lineId: string, host: 'A' | 'B') => {
      if (!podcastId) return
      const stateBefore = useEditorStore.getState().podcast
      const snapshot = stateBefore ? { ...stateBefore, podcastLines: [...stateBefore.podcastLines] } : null
      const target = snapshot?.podcastLines.find(l => l.id === lineId)
      if (target?.status === PodcastStatusEnum.Processing) return
      // optimistic update
      useEditorStore.setState(s => {
        const line = s.podcast?.podcastLines.find(l => l.id === lineId)
        if (line) {
          line.host = host
          line.audioFileUrl = null
          line.status = PodcastStatusEnum.Draft
        }
        if (s.podcast) {
          s.podcast.audioFileUrl = null // invalidate assembled audio
        }
      })
      try {
        await updatePodcastLine(podcastId, lineId, { host })
      } catch {
        // revert on failure
        if (snapshot) {
          useEditorStore.setState(s => { s.podcast = snapshot })
        }
        return
      }
      await refetch()
    },
    setLineEmotion: async (lineId: string, emotion: string) => {
      if (!podcastId) return
      // optimistic
      useEditorStore.setState(s => {
        const line = s.podcast?.podcastLines.find(l => l.id === lineId)
        if (line) {
          line.emotion = emotion
          line.audioFileUrl = null // invalidate audio
          line.status = PodcastStatusEnum.Draft
        }
        if (s.podcast) s.podcast.audioFileUrl = null
      })
      await updatePodcastLine(podcastId, lineId, { emotion })
      await refetch()
    },
    // removed undo/redo
    undo: () => { },
    redo: () => { },
    canUndo: false,
    canRedo: false,
  }), [store, refetch, refetchVoices, podcastId])

  return actions
}
